import { useEffect, useState } from "react";
import { verifyGaragePassword } from "../../api/garageAuth";
import logo from "../../assets/logo.png";
import { C, STATUS_STYLE, GARAGE_SERVICE_OPTIONS, useInventory, useResource, fetchBookings, createBooking, updateBooking, deleteBooking, updateBookingStatus, convertBookingToWorkOrder, fetchCustomers, fetchMechanics, createCustomer, updateCustomer, deleteCustomer, fetchWorkOrders, createWorkOrder, updateWorkOrder, deleteWorkOrder, updateWorkOrderStatus, createInvoiceFromWorkOrder, fetchQuotations, createQuotation, updateQuotation, deleteQuotation, updateQuotationStatus, convertQuotationToInvoice, fetchInvoices, createInvoice, updateInvoice, deleteInvoice, recordPayment, fetchServices, createService, updateService, deleteService, fetchReport, Badge, PrimaryBtn, OutlineBtn, DrawerShell, AvatarCircle, StatCard, Field, FInput, FTextarea, FSelect, Divider, Grid2, Grid3, Card, TH, TD, Msg, extractList, fmtPrice, fmtCompact } from "./common";
function RefSelect({ source, value, onChange, placeholder }) {
  const { data, loading } = useInventory(() => source());
  return (
    <FSelect value={value ?? ""} onChange={onChange}>
      <option value="">{loading ? "Loading..." : placeholder}</option>
      {data.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </FSelect>
  );
}

function LineItems({ value = [], onChange }) {
  const upd   = (i, k, v) => onChange(value.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const total = value.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {value.map((it, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 64px 110px 30px", gap: 8 }}>
          <FInput placeholder="Description" value={it.description} onChange={e => upd(i, "description", e.target.value)} />
          <FInput type="number" min="1" placeholder="Qty" value={it.qty} onChange={e => upd(i, "qty", e.target.value)} />
          <FInput type="number" min="0" placeholder="Unit price" value={it.unitPrice} onChange={e => upd(i, "unitPrice", e.target.value)} />
          <button onClick={() => onChange(value.filter((_, j) => j !== i))} style={{ border: `1px solid ${C.border}`, background: "transparent", borderRadius: 7, cursor: "pointer", color: C.muted }}>×</button>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <OutlineBtn onClick={() => onChange([...value, { description: "", qty: 1, unitPrice: "" }])}>Add line</OutlineBtn>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Total: {fmtPrice(total)}</span>
      </div>
    </div>
  );
}

const SERVICE_ITEMS = [
  "Engine Oil", "Oil Filter", "Air Cleaner Element", "Front Differential Oil",
  "Rear Differential Oil", "Transmission Oil", "Transfer Oil", "Fuel/Diesel Filter",
  "Greasing", "Power Steering Fluid", "Brake Fluid",
];

const itemKey = (name) => name.toLowerCase().replace(/[\s/]+/g, "_");
const DEFAULT_CHECKLIST = Object.fromEntries(SERVICE_ITEMS.map((name) => [itemKey(name), "none"]));

function CheckCell({ active, onClick }) {
  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <button type="button" onClick={onClick} style={{
        width: 22, height: 22, borderRadius: 4, border: `2px solid ${active ? C.red : C.border}`,
        background: active ? C.red : "transparent", cursor: "pointer", display: "flex",
        alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13,
        fontWeight: 700, flexShrink: 0,
      }}>
        {active ? "✓" : ""}
      </button>
    </div>
  );
}

function ServiceChecklist({ value = {}, onChange }) {
  const merged = { ...DEFAULT_CHECKLIST, ...value };
  const toggle = (key, column) => onChange({ ...merged, [key]: merged[key] === column ? "none" : column });
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px", background: "#f8fafc", borderBottom: `1px solid ${C.border}`, padding: "8px 12px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>
        <span>Item</span><span style={{ textAlign: "center" }}>Checked</span><span style={{ textAlign: "center" }}>Changed</span>
      </div>
      {SERVICE_ITEMS.map((name) => {
        const key = itemKey(name);
        return (
          <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px", padding: "8px 12px", borderBottom: `1px solid ${C.border}`, alignItems: "center", fontSize: 12 }}>
            <span>{name}</span>
            <CheckCell active={merged[key] === "checked"} onClick={() => toggle(key, "checked")} />
            <CheckCell active={merged[key] === "changed"} onClick={() => toggle(key, "changed")} />
          </div>
        );
      })}
    </div>
  );
}

function FieldInput({ f, value, set }) {
  const common = { value: value ?? "", onChange: e => set(f.key, e.target.value) };
  switch (f.type) {
    case "textarea": return <FTextarea placeholder={f.placeholder} {...common} />;
    case "checklist": return <ServiceChecklist value={value} onChange={v => set(f.key, v)} />;
    case "select":
      return (
        <FSelect {...common}>
          <option value="">Select</option>
          {f.options.map(o => <option key={o}>{o}</option>)}
        </FSelect>
      );
    case "customer": return <RefSelect source={fetchCustomers} placeholder="Select customer" {...common} />;
    case "mechanic": return <RefSelect source={fetchMechanics} placeholder="Unassigned"       {...common} />;
    case "items":    return <LineItems value={value} onChange={v => set(f.key, v)} />;
    default:         return <FInput type={f.type || "text"} placeholder={f.placeholder} {...common} />;
  }
}

const initialFor = (fields, record, defaults = {}) =>
  Object.fromEntries(fields.map(f => {
    if (f.type === "items") return [f.key, record?.[f.key] ?? []];
    if (f.type === "checklist") return [f.key, record?.[f.key] ?? { ...DEFAULT_CHECKLIST }];
    if (!record)            return [f.key, defaults[f.key] ?? ""];
    let v = record[f.key] ?? record[f.key.replace(/Id$/, "")]?.id ?? "";
    if (f.type === "date" && v) v = String(v).slice(0, 10);
    return [f.key, v];
  }));

const buildPayload = (fields, form) => {
  const out = {};
  fields.forEach(f => {
    const v = form[f.key];
    if (f.type === "items") {
      out[f.key] = (v || [])
        .filter(it => it.description?.trim())
        .map(it => ({ description: it.description.trim(), qty: Number(it.qty) || 1, unitPrice: Number(it.unitPrice) || 0 }));
      out.total = out[f.key].reduce((s, it) => s + it.qty * it.unitPrice, 0);
    } else if (f.type === "checklist") {
      out[f.key] = v ?? { ...DEFAULT_CHECKLIST };
    } else if (v !== "" && v != null) {
      out[f.key] = f.type === "number" ? Number(v) : v;
    }
  });
  return out;
};

function FormDrawer({ title, fields, initial, submitLabel, onSubmit, onClose }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    const missing = fields.find(f => f.required && (f.type === "items" ? !form[f.key]?.length : form[f.key] === "" || form[f.key] == null));
    if (missing) throw new Error(`${missing.label} is required.`);
    return onSubmit(buildPayload(fields, form));
  };

  return (
    <DrawerShell title={title} submitLabel={submitLabel} onSubmit={submit} onClose={onClose}>
      <Grid2>
        {fields.map(f => (
          <div key={f.key} style={f.wide ? { gridColumn: "1 / -1" } : undefined}>
            <Field label={f.label} required={f.required} hint={f.hint}>
              <FieldInput f={f} value={form[f.key]} set={set} />
            </Field>
          </div>
        ))}
      </Grid2>
    </DrawerShell>
  );
}

// ── Operations config ──────────────────────────────────────────
const custName = r => r.customerName ?? r.customer?.name ?? "—";
const mechName = r => r.mechanicName ?? r.mechanic?.name ?? "Unassigned";
const fmtDate  = d => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
const plateTag = p => p
  ? <span style={{ background: C.navy, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4 }}>{p}</span>
  : "—";

const PAY_FIELDS = [
  { key: "amount",    label: "Amount",    type: "number", required: true },
  { key: "method",    label: "Method",    type: "select", options: ["M-Pesa", "Cash", "Card", "Bank transfer"], required: true },
  { key: "reference", label: "Reference", placeholder: "e.g. M-Pesa code" },
];

const OPS = {
  bookings: {
    label: "Bookings", noun: "booking", addLabel: "Add New Booking",
    api: { list: fetchBookings, create: createBooking, update: updateBooking, remove: deleteBooking, setStatus: updateBookingStatus },
    statuses: ["Pending", "Confirmed", "Completed", "Cancelled"],
    defaults: { status: "Pending" },
    columns: [
      { label: "Customer",  render: custName },
      { label: "Vehicle",   render: r => <>{plateTag(r.plate)} <span style={{ marginLeft: 6 }}>{r.vehicle}</span></> },
      { label: "Service",   render: r => r.service },
      { label: "Date",      render: r => `${fmtDate(r.date)}${r.time ? ` · ${r.time}` : ""}` },
      { label: "Mechanic",  render: mechName },
      { label: "Status",    status: true },
    ],
    fields: [
      { key: "customerId", label: "Customer", type: "customer", required: true },
      { key: "service",    label: "Service",  type: "select", options: GARAGE_SERVICE_OPTIONS, required: true },
      { key: "plate",      label: "Plate",    placeholder: "KDA 123X" },
      { key: "vehicle",    label: "Vehicle",  placeholder: "Toyota Corolla 2018" },
      { key: "date",       label: "Date",     type: "date", required: true },
      { key: "time",       label: "Time",     type: "time" },
      { key: "mechanicId", label: "Mechanic", type: "mechanic" },
      { key: "status",     label: "Status",   type: "select", options: ["Pending", "Confirmed", "Completed", "Cancelled"] },
      { key: "notes",      label: "Notes",    type: "textarea", wide: true },
    ],
    actions: [{ label: "Start job", when: r => ["Pending", "Confirmed"].includes(r.status), run: r => convertBookingToWorkOrder(r.id) }],
  },

  workorders: {
    label: "Work Orders", noun: "work order",
    api: { list: fetchWorkOrders, create: createWorkOrder, update: updateWorkOrder, remove: deleteWorkOrder, setStatus: updateWorkOrderStatus },
    statuses: ["Pending", "In progress", "Waiting parts", "Delayed", "Completed"],
    defaults: { status: "Pending" },
    columns: [
      { label: "Job #",     render: r => r.number ?? r.id },
      { label: "Plate",     render: r => plateTag(r.plate) },
      { label: "Vehicle",   render: r => r.vehicle },
      { label: "Service",   render: r => r.service },
      { label: "Mechanic",  render: mechName },
      { label: "Status",    status: true },
      { label: "Est. cost", render: r => fmtPrice(r.estimatedCost) },
    ],
    fields: [
      { key: "customerId",    label: "Customer",  type: "customer", required: true },
      { key: "service",       label: "Service",   type: "select", options: GARAGE_SERVICE_OPTIONS, required: true },
      { key: "plate",         label: "Plate",     required: true, placeholder: "KDA 123X" },
      { key: "vehicle",       label: "Vehicle",   placeholder: "Toyota Corolla 2018" },
      { key: "mechanicId",    label: "Mechanic",  type: "mechanic" },
      { key: "status",        label: "Status",    type: "select", options: ["Pending", "In progress", "Waiting parts", "Delayed", "Completed"] },
      { key: "estimatedCost", label: "Estimated cost (KSh)", type: "number" },
      { key: "dueDate",       label: "Due date",  type: "date" },
      { key: "notes",         label: "Notes",     type: "textarea", wide: true },
    ],
    actions: [
      { label: "Invoice", when: r => r.status === "Completed", run: r => createInvoiceFromWorkOrder(r.id) },
      {
        label: "Create quotation",
        form: { title: "Create quotation from work order", submitLabel: "Create quotation",
          fields: [
            { key: "customerId", label: "Customer", type: "customer", required: true },
            { key: "name", label: "Name", required: true },
            { key: "plate", label: "Plate" }, { key: "vehicle", label: "Vehicle" },
            { key: "items", label: "Line items", type: "items", required: true, wide: true },
            { key: "notes", label: "Notes", type: "textarea", wide: true },
          ] },
        initial: r => ({ customerId: r.customerId ?? r.customer?.id ?? "", name: custName(r),
          plate: r.plate ?? "", vehicle: r.vehicle ?? "", items: [{ description: r.service || "Service", qty: 1, unitPrice: r.estimatedCost || 0 }], notes: "" }),
        submit: (_r, payload) => createQuotation(payload),
      },
    ],
  },

  services: {
    label: "Services", noun: "service",
    api: { list: fetchServices, create: createService, update: updateService, remove: deleteService },
    defaults: { serviceDate: new Date().toISOString().slice(0, 10), checklist: DEFAULT_CHECKLIST },
    columns: [
      { label: "Plate", render: r => plateTag(r.plate) },
      { label: "Vehicle", render: r => r.vehicle || "—" },
      { label: "Date", render: r => fmtDate(r.serviceDate) },
      { label: "Mileage", render: r => `${Number(r.mileage || 0).toLocaleString()} km` },
      { label: "Product", render: r => r.productUsed || "—" },
      { label: "Mechanic", render: mechName },
      { label: "Next svc", render: r => r.nextService ? `${Number(r.nextService).toLocaleString()} km` : "—" },
    ],
    fields: [
      { key: "customerId", label: "Customer", type: "customer" },
      { key: "plate", label: "Plate", required: true, placeholder: "KBT 00SX" },
      { key: "vehicle", label: "Vehicle", placeholder: "Toyota Land Cruiser 2020" },
      { key: "serviceDate", label: "Service date", required: true, type: "date" },
      { key: "mileage", label: "Current mileage (km)", required: true, type: "number", placeholder: "410075" },
      { key: "productUsed", label: "Product used", type: "select", options: GARAGE_SERVICE_OPTIONS },
      { key: "mechanicId", label: "Mechanic", type: "mechanic" },
      { key: "nextService", label: "Next service (km)", type: "number", placeholder: "415075" },
      { key: "checklist", label: "Service checklist", type: "checklist", wide: true },
      { key: "notes", label: "Notes", type: "textarea", wide: true },
    ],
  },

  quotations: {
    label: "Quotations", noun: "quotation",
    api: { list: fetchQuotations, create: createQuotation, update: updateQuotation, remove: deleteQuotation, setStatus: updateQuotationStatus },
    statuses: ["Draft", "Sent", "Accepted", "Rejected"],
    defaults: { status: "Draft" },
    columns: [
      { label: "Quote #",     render: r => r.number ?? r.id },
      { label: "Customer",    render: custName },
      { label: "Vehicle",     render: r => <>{plateTag(r.plate)} <span style={{ marginLeft: 6 }}>{r.vehicle}</span></> },
      { label: "Valid until", render: r => fmtDate(r.validUntil) },
      { label: "Total",       render: r => fmtPrice(r.total, r.currency) },
      { label: "Status",      status: true },
    ],
    fields: [
      { key: "customerId", label: "Customer",    type: "customer", required: true },
      { key: "name",       label: "Name",      required: true },
      { key: "plate",      label: "Plate" },
      { key: "vehicle",    label: "Vehicle" },
      { key: "validUntil", label: "Valid until", type: "date" },
      { key: "status",     label: "Status",      type: "select", options: ["Draft", "Sent", "Accepted", "Rejected"] },
      { key: "items",      label: "Line items",  type: "items", required: true, wide: true },
      { key: "notes",      label: "Notes",       type: "textarea", wide: true },
    ],
    actions: [{ label: "To invoice", when: r => r.status === "Accepted", run: r => convertQuotationToInvoice(r.id) }],
  },

  customers: {
    label: "Customers", noun: "customer", addLabel: "Add customer",
    api: { list: fetchCustomers, create: createCustomer, update: updateCustomer, remove: deleteCustomer },
    defaults: {},
    columns: [
      { label: "Name",  render: r => <div style={{ display: "flex", alignItems: "center", gap: 8 }}><AvatarCircle name={r.name} /><b>{r.name}</b></div> },
      { label: "Phone", render: r => r.phone },
      { label: "Email", render: r => r.email || "—" },
      { label: "Jobs",  render: r => r.totalJobs ?? 0 },
    ],
    fields: [
      { key: "name",    label: "Full name", required: true },
      { key: "phone",   label: "Phone",     required: true, placeholder: "+254 7XX XXX XXX" },
      { key: "email",   label: "Email",     type: "email" },
      { key: "address", label: "Address" },
      { key: "notes",   label: "Notes",     type: "textarea", wide: true },
    ],
  },

  invoices: {
    label: "Invoices", noun: "invoice", addLabel: "Add New Invoice",
    api: { list: fetchInvoices, create: createInvoice, update: updateInvoice, remove: deleteInvoice },
    statuses: ["Unpaid", "Partial", "Paid", "Overdue"],   // read-only: server derives status from payments
    defaults: {},
    columns: [
      { label: "Invoice #", render: r => r.number ?? r.id },
      { label: "Customer",  render: custName },
      { label: "Due",       render: r => fmtDate(r.dueDate) },
      { label: "Total",     render: r => fmtPrice(r.total, r.currency) },
      { label: "Paid",      render: r => fmtPrice(r.paid, r.currency) },
      { label: "Balance",   render: r => fmtPrice(r.balance ?? (Number(r.total || 0) - Number(r.paid || 0)), r.currency) },
      { label: "Status",    render: r => <Badge status={r.status} /> },
    ],
    fields: [
      { key: "customerId", label: "Customer",   type: "customer", required: true },
      { key: "plate",      label: "Plate" },
      { key: "dueDate",    label: "Due date",   type: "date" },
      { key: "items",      label: "Line items", type: "items", required: true, wide: true },
      { key: "notes",      label: "Notes",      type: "textarea", wide: true },
    ],
    actions: [{
      label: "Record payment",
      when: r => r.status !== "Paid",
      form: { title: "Record payment", fields: PAY_FIELDS, defaults: { method: "M-Pesa" }, submitLabel: "Save payment" },
      submit: (r, payload) => recordPayment(r.id, payload),
    }],
  },
};

function StatusSelect({ value, options, onChange }) {
  const s = STATUS_STYLE[value] || { bg: "#f1f5f9", color: C.muted };
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value)}
      style={{ background: s.bg, color: s.color, border: "none", borderRadius: 20, padding: "3px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
}

function OpsSection({ id, registerExport }) {
  const cfg = OPS[id];
  const [q, setQ]           = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [drawer, setDrawer] = useState(null); // { record?, action? }
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error, reload } = useResource(
    (signal) => cfg.api.list({ q: search, status }, signal),
    [id, search, status]
  );
  const rows = extractList(data);

  useEffect(() => { registerExport(cfg.label, extractList(data)); }, [data, cfg.label, registerExport]);

  const guard = async (fn, sensitive = true) => {
    if (sensitive) {
      const password = window.prompt("Re-enter your garage login password to continue.");
      if (!(await verifyGaragePassword(password))) { alert("Password verification failed."); return; }
    }
    try { await fn(); reload(); } catch (err) { alert(err.message || "Action failed."); }
  };
  const onDelete = (r) => window.confirm(`Delete this ${cfg.noun}? This cannot be undone.`) && guard(() => cfg.api.remove(r.id));
  const onAction = (a, r) => (a.form ? setDrawer({ action: a, record: r }) : guard(() => a.run(r)));
  useEffect(() => {
    const handler = async () => {
      if (!selected) { alert("Select a record with View first."); return; }
      const choice = window.prompt("Advanced tools: type edit or delete.");
      if (choice === "edit") {
        const password = window.prompt("Re-enter your garage login password to continue.");
        if (await verifyGaragePassword(password)) setDrawer({ record: selected });
        else alert("Password verification failed.");
      }
      if (choice === "delete") onDelete(selected);
    };
    window.addEventListener("garage-advanced-tools", handler);
    return () => window.removeEventListener("garage-advanced-tools", handler);
  });
  const closeDrawer = (saved) => { setDrawer(null); if (saved) reload(); };

  const renderDrawer = () => {
    if (!drawer) return null;
    const { record, action } = drawer;
    if (action) {
      return (
        <FormDrawer
          title={action.form.title} fields={action.form.fields} submitLabel={action.form.submitLabel}
          initial={action.initial ? action.initial(record) : initialFor(action.form.fields, null, action.form.defaults)}
          onSubmit={(p) => action.submit(record, p)} onClose={closeDrawer}
        />
      );
    }
    return (
      <FormDrawer
        title={record ? `Edit ${cfg.noun}` : `Add ${cfg.noun}`}
        fields={cfg.fields}
        initial={initialFor(cfg.fields, record, cfg.defaults)}
        onSubmit={(p) => (record ? cfg.api.update(record.id, p) : cfg.api.create(p))}
        onClose={closeDrawer}
      />
    );
  };

  const renderTable = () => {
    if (loading && !data) return <Msg>Loading {cfg.label.toLowerCase()}...</Msg>;
    if (error)            return <Msg color={C.red}>Error: {error}</Msg>;
    if (!rows.length)     return <Msg>No {cfg.label.toLowerCase()} found.</Msg>;
    return (
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead><tr>{[...cfg.columns.map(c => c.label), "Actions"].map(h => <TH key={h} c={h} />)}</tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ borderTop: `1px solid ${C.border}` }}>
              {cfg.columns.map(c => (
                <TD key={c.label}>
                  {c.status
                    ? (cfg.api.setStatus
                        ? <StatusSelect value={r.status} options={cfg.statuses} onChange={s => guard(() => cfg.api.setStatus(r.id, s))} />
                        : <Badge status={r.status} />)
                    : c.render(r)}
                </TD>
              ))}
              <TD>
                <div style={{ display: "flex", gap: 6 }}>
                  <OutlineBtn onClick={() => { setSelected(r); setDetail(r); }}>View</OutlineBtn>
                  {(cfg.actions || []).filter(a => !a.when || a.when(r)).map(a => (
                    <PrimaryBtn key={a.label} onClick={() => onAction(a, r)}>{a.label}</PrimaryBtn>
                  ))}
                </div>
              </TD>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderInvoicePrint = (invoice) => {
    if (id !== "invoices") return null;
    const items = Array.isArray(invoice.items) ? invoice.items : [];
    const total = Number(invoice.total) || 0;
    const paid = Number(invoice.paid) || 0;
    const balance = Number(invoice.balance ?? total - paid);
    return (
      <div className="invoice-print-document">
        <div className="invoice-print-header">
          <div>
            <div className="invoice-print-title">INVOICE</div>
            <div className="invoice-print-company">MY CAR KENYA</div>
          </div>
          <img src={logo} alt="My Car Kenya" className="invoice-print-logo" />
        </div>
        <div className="invoice-print-meta">
          <div>
            <span>ISSUED TO:</span>
            <strong>{invoice.customerName || "Customer"}</strong>
            {invoice.plate && <small>Vehicle registration: {invoice.plate}</small>}
          </div>
          <div className="invoice-print-meta-right">
            <p><span>INVOICE NO.</span><strong>{invoice.number || "—"}</strong></p>
            <p><span>ISSUED DATE</span><strong>{fmtDate(invoice.createdAt)}</strong></p>
            <p><span>DUE DATE</span><strong>{fmtDate(invoice.dueDate)}</strong></p>
          </div>
        </div>
        <table className="invoice-print-items">
          <thead><tr><th>DESCRIPTION</th><th>RATE</th><th>QTY</th><th>TOTAL</th></tr></thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.description}-${index}`}>
                <td>{item.description}</td>
                <td>{fmtPrice(item.unitPrice)}</td>
                <td>{item.qty}</td>
                <td>{fmtPrice((Number(item.qty) || 0) * (Number(item.unitPrice) || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="invoice-print-summary">
          <div><span>SUBTOTAL</span><strong>{fmtPrice(total)}</strong></div>
          {paid > 0 && <div><span>PAID</span><strong>{fmtPrice(paid)}</strong></div>}
          <div className="invoice-print-total"><span>BALANCE DUE</span><strong>{fmtPrice(balance)}</strong></div>
        </div>
        <div className="invoice-print-footer">
          <div>
            <span className="invoice-print-label">PAYMENT INFORMATION</span>
            <p>Payment should be made to the My Car Kenya account.<br />Please quote the invoice number as your reference.</p>
            {invoice.notes && <p>{invoice.notes}</p>}
          </div>
          <div className="invoice-print-signature">
            <div className="invoice-print-signature-line" />
            <span>Authorised signature</span>
          </div>
        </div>
        <p className="invoice-print-disclaimer">This invoice is invalid if not signed by an authorised representative of My Car Kenya.</p>
      </div>
    );
  };

  return (
    <div className={`page-section operations-page${detail && id === "invoices" ? " invoice-print-mode" : ""}`}>
      <div className="page-heading operations-heading">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>{cfg.label}</h2>
          <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>{loading ? "Loading..." : `${rows.length} found`}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ width: 200 }}><FInput placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} /></div>
          {cfg.statuses && (
            <div style={{ width: 140 }}>
              <FSelect value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {cfg.statuses.map(s => <option key={s}>{s}</option>)}
              </FSelect>
            </div>
          )}
          {cfg.addLabel && <PrimaryBtn onClick={() => setDrawer({})}>{cfg.addLabel}</PrimaryBtn>}
        </div>
      </div>
      <div className="table-shell operations-table-shell">
        {renderTable()}
      </div>
      {renderDrawer()}
      {detail && <div className="garage-detail-fullscreen" role="dialog">
        <div className="garage-detail-card">
          <div className="garage-detail-header"><h2>{cfg.label} detail</h2><div style={{ display: "flex", gap: 8 }}>{id === "invoices" && <PrimaryBtn onClick={() => window.print()}>Save as PDF</PrimaryBtn>}<OutlineBtn onClick={() => setDetail(null)}>Close</OutlineBtn></div></div>
          <dl>{Object.entries(detail).filter(([, v]) => v == null || typeof v !== "object").map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{String(v ?? "—")}</dd></div>)}</dl>
          {renderInvoicePrint(detail)}
        </div>
      </div>}
    </div>
  );
}

// ── Reports ────────────────────────────────────────────────────
function Reports({ registerExport }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to,   setTo]   = useState(today);

  const { data, loading, error } = useResource((signal) => fetchReport({ from, to }, signal), [from, to]);
  useEffect(() => { registerExport("revenue-report", data?.revenueByMonth ?? []); }, [data, registerExport]);

  const t       = data?.totals ?? {};
  const monthly = data?.revenueByMonth ?? [];
  const maxRev  = Math.max(1, ...monthly.map(m => Number(m.revenue) || 0));

  const simpleTable = (rows, cols) => !rows?.length ? <Msg>No data for this period.</Msg> : (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr>{cols.map(c => <TH key={c.label} c={c.label} />)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>{cols.map(c => <TD key={c.label}>{c.render(r)}</TD>)}</tr>
      ))}</tbody>
    </table>
  );

  return (
    <div className="page-section reports-page">
      <div className="report-print-header">
        <img src={logo} alt="My Car Kenya" />
        <div><span>MY CAR KENYA</span><h1>Garage performance report</h1><p>{from} to {to}</p></div>
      </div>
      <div className="report-filters">
        <div style={{ width: 160 }}><FInput type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} /></div>
        <span style={{ color: C.muted, fontSize: 12 }}>to</span>
        <div style={{ width: 160 }}><FInput type="date" value={to} min={from} onChange={e => setTo(e.target.value)} /></div>
      </div>

      {loading && !data ? <Msg>Loading report...</Msg> : error ? <Msg color={C.red}>Error: {error}</Msg> : (
        <>
          <div className="report-stats">
            <StatCard label="Revenue"          value={fmtCompact(t.revenue)} accent />
            <StatCard label="Jobs completed"   value={t.jobsCompleted ?? 0} />
            <StatCard label="Invoices issued"  value={t.invoicesIssued ?? 0} />
            <StatCard label="Outstanding"      value={fmtCompact(t.outstanding)} accent />
          </div>

          <Card title="Revenue by month">
            {!monthly.length ? <Msg>No revenue in this period.</Msg> : monthly.map(m => (
              <div key={m.month} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                <span style={{ fontSize: 12, flex: "0 0 80px" }}>{m.month}</span>
                <div style={{ flex: 1, height: 8, background: C.border, borderRadius: 4 }}>
                  <div style={{ width: `${(Number(m.revenue) / maxRev) * 100}%`, height: "100%", background: C.red, opacity: .75, borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, flex: "0 0 90px", textAlign: "right" }}>{fmtCompact(m.revenue)}</span>
              </div>
            ))}
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
            <Card title="Top services">
              {simpleTable(data?.topServices, [
                { label: "Service", render: r => r.name }, { label: "Jobs", render: r => r.count }, { label: "Revenue", render: r => fmtPrice(r.revenue) },
              ])}
            </Card>
            <Card title="Mechanic performance">
              {simpleTable(data?.mechanicPerformance, [
                { label: "Mechanic", render: r => r.name }, { label: "Jobs done", render: r => r.jobsDone }, { label: "Revenue", render: r => fmtPrice(r.revenue) },
              ])}
            </Card>
          </div>

          <div style={{ marginTop: 14 }}>
            <Card title="Low stock" sub="spares & accessories">
              {simpleTable(data?.lowStock, [
                { label: "Item", render: r => r.name }, { label: "Type", render: r => r.type },
                { label: "Left", render: r => <b style={{ color: C.red }}>{r.quantity}</b> },
              ])}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ── CSV export of whatever the current page has loaded ─────────

export { OPS, OpsSection, Reports };
